import React from "react";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";

const LANGS = [
  { code: "bg", label: "BG" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "parameters", label: "Parameters" },
  { value: "nutrition", label: "Nutrition" },
  { value: "research", label: "Research" },
  { value: "guides", label: "Guides" },
];

const suffixFor = (code) => (code === "en" ? "" : `_${code}`);

/** Single-row group of i18n input fields */
function LocalizedFields({ form, setForm, langTab, isDark, inputCls, textareaCls }) {
  const sfx = suffixFor(langTab);
  const titleKey = `title${sfx}`;
  const excerptKey = `excerpt${sfx}`;
  const contentKey = `content${sfx}`;
  const lang = langTab.toUpperCase();
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-medium mb-1 block">Title ({lang})</label>
          <input
            className={inputCls}
            value={form[titleKey] || ""}
            onChange={(e) => setField(titleKey, e.target.value)}
            data-testid="article-title-input"
            placeholder={form.title || "Article title..."}
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Slug</label>
          <input
            className={inputCls}
            value={form.slug}
            onChange={(e) => setField("slug", e.target.value)}
            placeholder="auto-generated"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Excerpt ({lang})</label>
        <input
          className={inputCls}
          value={form[excerptKey] || ""}
          onChange={(e) => setField(excerptKey, e.target.value)}
          placeholder={form.excerpt || "Short description..."}
        />
      </div>
      <div>
        <label className="text-xs font-medium mb-1 block">Content ({lang}) — Markdown</label>
        <textarea
          className={textareaCls}
          rows={8}
          value={form[contentKey] || ""}
          onChange={(e) => setField(contentKey, e.target.value)}
          data-testid="article-content-input"
          placeholder="Write article content in Markdown..."
        />
      </div>
    </>
  );
}

/** Category + tags + cover image row */
function MetaFields({ form, setForm, inputCls }) {
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <label className="text-xs font-medium mb-1 block">Category</label>
        <select className={inputCls} value={form.category} onChange={(e) => setField("category", e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium mb-1 block">Tags (comma)</label>
        <input
          className={inputCls}
          value={(form.tags || []).join(", ")}
          onChange={(e) => setField("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
        />
      </div>
      <div>
        <label className="text-xs font-medium mb-1 block">Cover Image URL</label>
        <input
          className={inputCls}
          value={form.cover_image || ""}
          onChange={(e) => setField("cover_image", e.target.value)}
          placeholder="https://..."
        />
      </div>
    </div>
  );
}

/** Collapsible SEO block per language */
function SeoBlock({ form, setForm, langTab, showSeo, setShowSeo, isDark, inputCls }) {
  const sfx = suffixFor(langTab);
  const seoTitleKey = `seo_title${sfx}`;
  const seoDescKey = `seo_description${sfx}`;
  const titleKey = `title${sfx}`;
  const excerptKey = `excerpt${sfx}`;
  const lang = langTab.toUpperCase();
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <div>
      <button
        onClick={() => setShowSeo(!showSeo)}
        className={`flex items-center gap-1 text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-500"}`}
        data-testid="toggle-seo-btn"
      >
        <Globe className="w-3 h-3" /> SEO ({lang}) {showSeo ? "▲" : "▼"}
      </button>
      {showSeo && (
        <div className="mt-2 space-y-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
          <div>
            <label className="text-[10px] font-medium mb-0.5 block text-blue-600">SEO Title ({lang})</label>
            <input
              className={inputCls}
              value={form[seoTitleKey] || ""}
              onChange={(e) => setField(seoTitleKey, e.target.value)}
              placeholder={`${form[titleKey] || form.title || "Title"} | Nutramilo`}
              data-testid="seo-title-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium mb-0.5 block text-blue-600">SEO Description ({lang})</label>
            <input
              className={inputCls}
              value={form[seoDescKey] || ""}
              onChange={(e) => setField(seoDescKey, e.target.value)}
              placeholder={form[excerptKey] || form.excerpt || "Meta description..."}
              data-testid="seo-desc-input"
            />
          </div>
          <p className="text-[9px] text-blue-400">Auto-generated from title/excerpt if left empty.</p>
        </div>
      )}
    </div>
  );
}

/** Full create/edit form */
export default function ArticleForm({ form, setForm, langTab, setLangTab, showSeo, setShowSeo, onCancel, onSave, isEditing, isDark }) {
  const inputCls = `w-full px-3 py-2 rounded-lg text-sm border ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`;
  const textareaCls = `${inputCls} min-h-[120px] resize-y font-mono text-xs`;

  return (
    <div
      className={`p-4 rounded-xl border mb-4 space-y-3 ${isDark ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}
      data-testid="article-form"
    >
      {/* Language tabs */}
      <div className="flex items-center gap-1 border-b pb-2 mb-2" data-testid="lang-tabs">
        {LANGS.map((l) => {
          const hasTranslation = Boolean(form[`title${suffixFor(l.code)}`]);
          const isActive = langTab === l.code;
          return (
            <button
              key={l.code}
              onClick={() => setLangTab(l.code)}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                isActive
                  ? "bg-[#6366F1] text-white"
                  : isDark ? "text-gray-400 hover:bg-gray-600" : "text-gray-500 hover:bg-gray-200"
              }`}
              data-testid={`lang-tab-${l.code}`}
            >
              {l.label}
              {hasTranslation && <span className="ml-1 w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />}
            </button>
          );
        })}
      </div>

      <LocalizedFields form={form} setForm={setForm} langTab={langTab} isDark={isDark} inputCls={inputCls} textareaCls={textareaCls} />
      <MetaFields form={form} setForm={setForm} inputCls={inputCls} />
      <SeoBlock form={form} setForm={setForm} langTab={langTab} showSeo={showSeo} setShowSeo={setShowSeo} isDark={isDark} inputCls={inputCls} />

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
            className="rounded"
          />
          Published
        </label>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" className="gradient-primary text-white" onClick={onSave} data-testid="save-article-btn">
            {isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { LANGS, suffixFor };
